import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { updateUserProfile, changeUserPassword } from '../api/userService';
import chartStoryLogo from '../asset/Chart Story 72.png';
import './UserProfile.css';

const ROLE_LABELS = { admin: 'Administrator', analyst: 'Analyst', viewer: 'Viewer' };
const ROLE_COLORS = { admin: 'role-admin', analyst: 'role-analyst', viewer: 'role-viewer' };

const PAGE_LABELS = {
  marketOverview: 'Market Overview',
  stockOverview: 'Stock Overview',
  sectorAnalysis: 'Sector Analysis',
  strategy: 'Strategy',
  strategyAnalysis: 'Strategy Analysis',
  astrology: 'Astrology',
  astroNakshatra: 'Astro Nakshatra',
  adminPanel: 'Admin Panel',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const UserProfile = () => {
  const { user, setUser, logoutAction } = useAuth();
  const navigate = useNavigate();

  // Profile edit state
  const [editMode, setEditMode] = useState(false);
  const [nameValue, setNameValue] = useState(user?.name || '');
  const [phoneValue, setPhoneValue] = useState(user?.phone || '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ text: '', type: '' });

  // Affirmation / shloka scrolling banner
  const [affText, setAffText] = useState(user?.affirmation?.text || '');
  const [affEnabled, setAffEnabled] = useState(!!user?.affirmation?.enabled);
  const [affLoading, setAffLoading] = useState(false);
  const [affMsg, setAffMsg] = useState({ text: '', type: '' });

  const handleAffirmationSave = async (e) => {
    e.preventDefault();
    setAffLoading(true);
    setAffMsg({ text: '', type: '' });
    try {
      const { data } = await updateUserProfile({
        affirmation: { text: affText.trim(), enabled: affEnabled },
      });
      setUser(data.user);
      setAffMsg({ text: 'Affirmation saved.', type: 'success' });
    } catch (err) {
      setAffMsg({ text: err.response?.data?.message || 'Failed to save affirmation.', type: 'error' });
    } finally {
      setAffLoading(false);
    }
  };

  // Password change state
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMsg, setPwMsg] = useState({ text: '', type: '' });
  const [showPw, setShowPw] = useState({ current: false, new: false, confirm: false });

  const nameInputRef = useRef(null);

  const handleEditToggle = () => {
    setEditMode((v) => !v);
    setNameValue(user?.name || '');
    setPhoneValue(user?.phone || '');
    setProfileMsg({ text: '', type: '' });
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!nameValue.trim()) return;

    const phoneDigits = phoneValue.replace(/[\s\-()]/g, '');
    if (phoneDigits && !/^\+?\d{10,15}$/.test(phoneDigits)) {
      setProfileMsg({ text: 'Please enter a valid phone number (10-15 digits, optional country code).', type: 'error' });
      return;
    }

    setProfileLoading(true);
    setProfileMsg({ text: '', type: '' });
    try {
      const { data } = await updateUserProfile({
        name: nameValue.trim(),
        phone: phoneDigits,
      });
      setUser(data.user);
      setEditMode(false);
      setProfileMsg({ text: 'Profile updated successfully.', type: 'success' });
    } catch (err) {
      setProfileMsg({ text: err.response?.data?.message || 'Failed to update profile.', type: 'error' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePwChange = (e) => {
    setPwForm({ ...pwForm, [e.target.name]: e.target.value });
    setPwMsg({ text: '', type: '' });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      setPwMsg({ text: 'New passwords do not match.', type: 'error' });
      return;
    }
    if (pwForm.newPassword.length < 8) {
      setPwMsg({ text: 'Password must be at least 8 characters.', type: 'error' });
      return;
    }
    setPwLoading(true);
    setPwMsg({ text: '', type: '' });
    try {
      await changeUserPassword({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword });
      setPwMsg({ text: 'Password changed successfully.', type: 'success' });
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPwMsg({ text: err.response?.data?.message || 'Failed to change password.', type: 'error' });
    } finally {
      setPwLoading(false);
    }
  };

  const handleLogout = async () => {
    await logoutAction();
    navigate('/login');
  };

  const pageAccess = user?.pageAccess || {};
  const accessiblePages = Object.entries(pageAccess).filter(([, v]) => v);
  const totalPages = Object.keys(PAGE_LABELS).length;

  const avatarInitials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '??';

  return (
    <div className="profile-page">
      {/* ── HEADER ── */}
      <header className="profile-header">
        <div className="profile-header-inner">
          <div className="profile-header-logo" onClick={() => navigate('/market-insight')} title="Go to Market Insight">
            <img src={chartStoryLogo} alt="Chart Story" className="profile-logo-img" />
          </div>
          {user?.affirmation?.enabled && user?.affirmation?.text?.trim() && (
            <div className="header-affirmation" aria-label="Personal affirmation">
              <div className="header-affirmation__track">
                <span className="header-affirmation__text">{user.affirmation.text.trim()}</span>
                <span className="header-affirmation__text" aria-hidden="true">{user.affirmation.text.trim()}</span>
              </div>
            </div>
          )}
          <h2 className="profile-header-title">My Profile</h2>
          <button className="btn-back" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
      </header>

      <main className="profile-main">
        <div className="profile-grid">

          {/* ── LEFT COLUMN ── */}
          <div className="profile-col-left">

            {/* Avatar Card */}
            <div className="profile-card avatar-card">
              <div className="avatar-circle">{avatarInitials}</div>
              <h2 className="avatar-name">{user?.name}</h2>
              <span className={`role-badge ${ROLE_COLORS[user?.role] || ''}`}>
                {ROLE_LABELS[user?.role] || user?.role}
              </span>
              <div className="avatar-status-row">
                <span className={`status-dot ${user?.isActive ? 'active' : 'inactive'}`} />
                <span className="status-label">{user?.isActive ? 'Active' : 'Inactive'}</span>
                <span className="separator">•</span>
                <span className={`verify-badge ${user?.isEmailVerified ? 'verified' : 'unverified'}`}>
                  {user?.isEmailVerified ? '✓ Email Verified' : '✗ Not Verified'}
                </span>
              </div>
              <button className="btn-logout" onClick={handleLogout}>Sign Out</button>
            </div>

            {/* Account Stats Card */}
            <div className="profile-card stats-card">
              <h3 className="card-title">Account Info</h3>
              <div className="stat-row">
                <span className="stat-label">Member Since</span>
                <span className="stat-value">{formatDate(user?.createdAt)}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Last Login</span>
                <span className="stat-value">{formatDate(user?.lastLogin)}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Email</span>
                <span className="stat-value email-val">{user?.email}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Phone</span>
                <span className="stat-value">{user?.phone || '—'}</span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Pages Accessible</span>
                <span className="stat-value">
                  <span className="access-count">{accessiblePages.length}</span>
                  <span className="access-total"> / {totalPages}</span>
                </span>
              </div>
            </div>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="profile-col-right">

            {/* Edit Profile Card */}
            <div className="profile-card">
              <div className="card-title-row">
                <h3 className="card-title">Edit Profile</h3>
                <button className="btn-edit-toggle" onClick={handleEditToggle}>
                  {editMode ? 'Cancel' : 'Edit'}
                </button>
              </div>

              {profileMsg.text && (
                <div className={`profile-msg ${profileMsg.type}`}>{profileMsg.text}</div>
              )}

              <form onSubmit={handleProfileSave} className="profile-form">
                <div className="form-field">
                  <label>Full Name</label>
                  <input
                    ref={nameInputRef}
                    type="text"
                    value={editMode ? nameValue : user?.name || ''}
                    onChange={(e) => setNameValue(e.target.value)}
                    disabled={!editMode}
                    className={editMode ? 'editable' : ''}
                    placeholder="Your full name"
                  />
                </div>
                <div className="form-field">
                  <label>Email Address</label>
                  <input type="email" value={user?.email || ''} disabled />
                  <span className="field-note">Email cannot be changed.</span>
                </div>
                <div className="form-field">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={editMode ? phoneValue : user?.phone || ''}
                    onChange={(e) => setPhoneValue(e.target.value)}
                    disabled={!editMode}
                    className={editMode ? 'editable' : ''}
                    placeholder="+91 98765 43210"
                  />
                  {editMode && <span className="field-note">10-15 digits, optional country code.</span>}
                </div>
                <div className="form-field">
                  <label>Role</label>
                  <input type="text" value={ROLE_LABELS[user?.role] || user?.role || ''} disabled />
                  <span className="field-note">Role is managed by administrators.</span>
                </div>
                {editMode && (
                  <button type="submit" className="btn-save" disabled={profileLoading}>
                    {profileLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </form>
            </div>

            {/* Affirmation / Shloka Banner Card */}
            <div className="profile-card">
              <div className="card-title-row">
                <h3 className="card-title">Scrolling Affirmation / Shloka</h3>
                <label className="aff-toggle" title={affEnabled ? 'Enabled' : 'Disabled'}>
                  <input
                    type="checkbox"
                    checked={affEnabled}
                    onChange={(e) => setAffEnabled(e.target.checked)}
                  />
                  <span className="aff-toggle-slider" />
                  <span className="aff-toggle-label">{affEnabled ? 'Enabled' : 'Disabled'}</span>
                </label>
              </div>
              <p className="card-subtitle">
                Shown as a scrolling banner in the top header between the logo and the page badge.
                Leave empty or disable to hide it.
              </p>

              {affMsg.text && (
                <div className={`profile-msg ${affMsg.type}`}>{affMsg.text}</div>
              )}

              <form onSubmit={handleAffirmationSave} className="profile-form">
                <div className="form-field">
                  <label>Your Affirmation or Shloka</label>
                  <textarea
                    rows={3}
                    maxLength={500}
                    value={affText}
                    onChange={(e) => setAffText(e.target.value)}
                    className="editable aff-textarea"
                    placeholder="e.g. ॐ गं गणपतये नमः  •  I trade with discipline and patience."
                  />
                  <span className="field-note">{affText.length}/500 characters.</span>
                </div>

                {affEnabled && affText.trim() && (
                  <div className="form-field">
                    <label>Live Preview (as shown in header)</label>
                    <div className="header-affirmation aff-preview" aria-hidden="true">
                      <div className="header-affirmation__track">
                        <span className="header-affirmation__text">{affText.trim()}</span>
                        <span className="header-affirmation__text">{affText.trim()}</span>
                      </div>
                    </div>
                  </div>
                )}

                <button type="submit" className="btn-save" disabled={affLoading}>
                  {affLoading ? 'Saving...' : 'Save Affirmation'}
                </button>
              </form>
            </div>

            {/* Change Password Card */}
            <div className="profile-card">
              <h3 className="card-title">Change Password</h3>

              {pwMsg.text && (
                <div className={`profile-msg ${pwMsg.type}`}>{pwMsg.text}</div>
              )}

              <form onSubmit={handlePasswordSubmit} className="profile-form">
                {['currentPassword', 'newPassword', 'confirmPassword'].map((field) => {
                  const labels = { currentPassword: 'Current Password', newPassword: 'New Password', confirmPassword: 'Confirm New Password' };
                  const key = field === 'currentPassword' ? 'current' : field === 'newPassword' ? 'new' : 'confirm';
                  return (
                    <div className="form-field" key={field}>
                      <label>{labels[field]}</label>
                      <div className="pw-input-wrapper">
                        <input
                          type={showPw[key] ? 'text' : 'password'}
                          name={field}
                          value={pwForm[field]}
                          onChange={handlePwChange}
                          placeholder="••••••••"
                          autoComplete={field === 'currentPassword' ? 'current-password' : 'new-password'}
                        />
                        <button
                          type="button"
                          className="pw-toggle"
                          onClick={() => setShowPw((s) => ({ ...s, [key]: !s[key] }))}
                          tabIndex={-1}
                        >
                          {showPw[key] ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>
                  );
                })}
                <button type="submit" className="btn-save" disabled={pwLoading}>
                  {pwLoading ? 'Changing...' : 'Change Password'}
                </button>
              </form>
            </div>

            {/* Page Access Card */}
            <div className="profile-card">
              <h3 className="card-title">Page Access</h3>
              <p className="card-subtitle">Pages you currently have access to</p>
              <div className="access-grid">
                {Object.entries(PAGE_LABELS).map(([key, label]) => {
                  const hasAccess = user?.role === 'admin' || pageAccess[key] === true;
                  return (
                    <div key={key} className={`access-chip ${hasAccess ? 'granted' : 'denied'}`}>
                      <span className="access-icon">{hasAccess ? '✓' : '✗'}</span>
                      {label}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default UserProfile;
